import type { ReactNode } from "react"
import { useState } from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
	IconLayersOutline18 as Some,
	IconTriangleWarningOutline18 as Warning,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	AvatarStack,
	Badge,
	Button,
	Checkbox,
	IssueStatusIcon,
	SegmentedControl,
	Switch,
} from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

export interface PortalOption {
	id: string
	name: string
	// Why customers on this portal would still not see the item, if anything.
	blocker?: string
}

export const PORTALS: PortalOption[] = [
	{ id: "main", name: "Main portal" },
	{ id: "acme", name: "Acme Enterprise" },
	{
		id: "partners",
		name: "Partners",
		blocker: "Feature requests are off here",
	},
]

export function summarize(selected: string[], portals: PortalOption[]) {
	if (selected.length === 0) return "Hidden"
	if (selected.length === portals.length) return "All portals"
	const names = portals
		.filter((p) => selected.includes(p.id))
		.map((p) => p.name)
	if (names.length === 1) return names[0] ?? "Hidden"
	return `${names.length} portals`
}

function useSelection(initial: string[]) {
	const [selected, setSelected] = useState(initial)
	const toggle = (id: string) =>
		setSelected((prev) =>
			prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
		)
	return { selected, setSelected, toggle }
}

const MENU =
	"rounded-lg bg-white p-1 shadow-md ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10"

function PortalAvatar({ name, dim }: { name: string; dim?: boolean }) {
	return (
		<Avatar
			name={name}
			roundness="sm"
			size="xs"
			className={cn("flex-none", dim && "opacity-30 grayscale")}
		/>
	)
}

// 1. Portal chips: every portal is a chip you click. Off chips stay in place, faded.
export function ControlPortalChips({ portals }: { portals: PortalOption[] }) {
	const { selected, toggle } = useSelection(["main", "acme"])
	return (
		<div className="flex flex-col gap-2">
			<div className="text-xs text-tertiary">Shown on</div>
			<div className="flex flex-wrap gap-1.5">
				{portals.map((p) => {
					const on = selected.includes(p.id)
					return (
						<Button
							key={p.id}
							variant={on ? "outline" : "dashed"}
							size="sm"
							Icon={() => <PortalAvatar name={p.name} dim={!on} />}
							className={cn(!on && "text-tertiary")}
							onClick={() => toggle(p.id)}
						>
							{p.name}
						</Button>
					)
				})}
			</div>
			<div className="text-xxs text-tertiary">
				{selected.length === 0
					? "Hidden from every portal"
					: `Customers on ${selected.length} of ${portals.length} portals can upvote it`}
			</div>
		</div>
	)
}

// 2. One menu, one list. "Visible" and "Hidden" go away: no portal checked is hidden.
export function ControlChecklistMenu({ portals }: { portals: PortalOption[] }) {
	const { selected, setSelected, toggle } = useSelection(["main", "partners"])
	return (
		<div className="flex flex-col items-end gap-1.5">
			<Button
				variant="outline"
				wrapperClassName="gap-1.5"
				iconRight={ChevronDown}
				Icon={selected.length === 0 ? EyeOff : undefined}
			>
				{selected.length > 0 && (
					<AvatarStack
						size="xxs"
						avatars={portals
							.filter((p) => selected.includes(p.id))
							.map((p) => ({ name: p.name }))}
					/>
				)}
				{summarize(selected, portals)}
			</Button>
			<div className={cn(MENU, "w-64")}>
				<div className="px-2 pb-1 pt-1.5 text-xs font-medium text-secondary">
					Show on
				</div>
				{portals.map((p) => (
					<label
						key={p.id}
						className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<Checkbox
							size="sm"
							checked={selected.includes(p.id)}
							onCheckedChange={() => toggle(p.id)}
						/>
						<PortalAvatar name={p.name} />
						<span className="min-w-0 flex-1 truncate">{p.name}</span>
						{p.blocker && (
							<Warning className="h-3.5 w-3.5 flex-none text-amber-500" />
						)}
					</label>
				))}
				<div className="my-1 border-t border-gray-200 dark:border-gray-700/80" />
				<button
					type="button"
					onClick={() => setSelected([])}
					className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-secondary hover:bg-gray-100 dark:hover:bg-gray-800"
				>
					<EyeOff className="h-3.5 w-3.5 flex-none" />
					Hide everywhere
				</button>
			</div>
		</div>
	)
}

const ROWS = [
	{ id: "PRO-412", title: "Export dashboards to CSV", status: "unstarted" },
	{ id: "PRO-388", title: "Scheduled email reports", status: "backlog" },
	{ id: "PRO-301", title: "SAML for every plan", status: "started" },
	{ id: "PRO-277", title: "Dark mode for the widget", status: "triage" },
]

// 3. Matrix: the feature requests screen shows every portal as a column, so the
// switch never depends on a portal picked somewhere else.
export function ControlMatrix({ portals }: { portals: PortalOption[] }) {
	const [cells, setCells] = useState<Record<string, boolean>>({
		"PRO-412:main": true,
		"PRO-412:acme": true,
		"PRO-388:main": true,
		"PRO-301:acme": true,
		"PRO-301:partners": true,
	})
	return (
		<div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700/80">
			<div className="grid grid-cols-[1fr_repeat(3,64px)] items-center border-b border-gray-200 bg-gray-50 px-3 py-2 text-xxs text-tertiary dark:border-gray-700/80 dark:bg-gray-800/40">
				<span>Request</span>
				{portals.map((p) => (
					<span key={p.id} className="flex justify-center" title={p.name}>
						<PortalAvatar name={p.name} />
					</span>
				))}
			</div>
			{ROWS.map((r) => (
				<div
					key={r.id}
					className="grid grid-cols-[1fr_repeat(3,64px)] items-center border-b border-gray-200 px-3 py-2 last:border-b-0 dark:border-gray-700/80"
				>
					<div className="flex min-w-0 items-center gap-1.5">
						<IssueStatusIcon status={r.status} className="h-3.5 w-3.5" />
						<span className="truncate text-xs text-primary">{r.title}</span>
						{!portals.some((p) => cells[`${r.id}:${p.id}`]) && (
							<Badge variant="light-gray" size="sm" className="flex-none">
								Hidden
							</Badge>
						)}
					</div>
					{portals.map((p) => {
						const key = `${r.id}:${p.id}`
						return (
							<span key={p.id} className="flex justify-center">
								<Switch
									name={key}
									size="small"
									checked={cells[key] ?? false}
									onCheckedChange={(v) =>
										setCells((prev) => ({ ...prev, [key]: v }))
									}
								/>
							</span>
						)
					})}
				</div>
			))}
		</div>
	)
}

type Scope = "hidden" | "some" | "all"

// 4. Three states up front. Picking "Some" opens the list below.
export function ControlScopeSegments({ portals }: { portals: PortalOption[] }) {
	const [scope, setScope] = useState<Scope>("some")
	const { selected, toggle } = useSelection(["acme"])
	return (
		<div className="flex flex-col gap-2">
			<SegmentedControl<Scope>
				name="scope"
				value={scope}
				onValueChange={setScope}
				showLabels
				options={[
					{ value: "hidden", label: "Hidden", Icon: EyeOff },
					{ value: "some", label: "Some", Icon: Some },
					{ value: "all", label: "All", Icon: Eye },
				]}
			/>
			<div
				className={cn(
					"flex flex-col gap-1 rounded-md p-1 ring-1 ring-gray-200 dark:ring-gray-700/80",
					scope !== "some" && "pointer-events-none opacity-40",
				)}
			>
				{portals.map((p) => (
					<Checkbox
						key={p.id}
						size="sm"
						className="px-1.5 py-1"
						label={p.name}
						checked={
							scope === "all" || (scope === "some" && selected.includes(p.id))
						}
						onCheckedChange={() => toggle(p.id)}
					/>
				))}
			</div>
			<div className="text-xxs text-tertiary">
				{scope === "all"
					? "Includes portals you add later."
					: scope === "hidden"
						? "Only your team sees it."
						: `Shown on ${selected.length} portal${selected.length === 1 ? "" : "s"}.`}
			</div>
		</div>
	)
}

// 5. A Portals property in the detail sidebar: one switch per portal, and the
// reason a portal still would not show it.
export function ControlPropertyPanel({ portals }: { portals: PortalOption[] }) {
	const { selected, toggle } = useSelection(["main", "partners"])
	return (
		<div className="flex flex-col gap-1">
			<div className="px-2 pb-1 text-xs font-medium text-tertiary">Portals</div>
			{portals.map((p) => {
				const on = selected.includes(p.id)
				return (
					<div key={p.id} className="rounded-md px-2 py-1.5">
						<div className="flex items-center gap-2">
							<PortalAvatar name={p.name} />
							<span className="min-w-0 flex-1 truncate text-xs text-primary">
								{p.name}
							</span>
							<Switch
								name={`panel-${p.id}`}
								size="small"
								checked={on}
								onCheckedChange={() => toggle(p.id)}
							/>
						</div>
						{on && p.blocker && (
							<div className="mt-1 flex items-center gap-1 pl-7 text-xxs text-amber-600 dark:text-amber-400">
								<Warning className="h-3 w-3 flex-none" />
								<span className="truncate">{p.blocker}.</span>
								<button type="button" className="flex-none underline">
									Turn on
								</button>
							</div>
						)}
					</div>
				)
			})}
		</div>
	)
}

function Frame({ children, wide }: { children: ReactNode; wide?: boolean }) {
	return (
		<div
			className={cn(
				"rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700/80 dark:bg-gray-900",
				wide ? "w-[460px]" : "w-[300px]",
			)}
		>
			{children}
		</div>
	)
}

function Variant({
	label,
	note,
	wide,
	children,
}: {
	label: string
	note: string
	wide?: boolean
	children: ReactNode
}) {
	return (
		<div className={cn("space-y-2", wide ? "w-[460px]" : "w-[300px]")}>
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="min-h-[48px] text-xxs text-tertiary">{note}</div>
			<Frame wide={wide}>{children}</Frame>
		</div>
	)
}

export function PortalVisibilityControlsPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen bg-gray-50 p-10 dark:bg-gray-950">
			<div className="mb-8 max-w-2xl">
				<div className="text-lg font-semibold text-primary">
					Feature request visibility with several portals, five options
				</div>
				<div className="mt-1 text-sm text-secondary">
					Today the detail page says Visible or Hidden, the list switch means
					&quot;on the portal picked in the sidebar&quot;, and the per-portal
					list hides in a hover card. Every option below shows the portals
					themselves, so the state reads without a hover. All are clickable.
				</div>
			</div>
			<div className="flex flex-wrap items-start gap-8">
				<Variant
					label="1. Portal chips"
					note="Every portal is a chip on the detail page. Off chips stay put, faded, so nothing shifts."
				>
					<ControlPortalChips portals={PORTALS} />
				</Variant>
				<Variant
					label="2. One checklist"
					note="The button names where it shows. The menu is a checklist; nothing checked means hidden. A warning marks portals with requests off."
				>
					<ControlChecklistMenu portals={PORTALS} />
				</Variant>
				<Variant
					label="3. Portal columns"
					wide
					note="For the feature requests screen. One column per portal replaces the portal tree and the active-portal switch."
				>
					<ControlMatrix portals={PORTALS} />
				</Variant>
				<Variant
					label="4. Hidden, some, all"
					note="The three answers people expect. All portals also covers ones added later (needs a flag)."
				>
					<ControlScopeSegments portals={PORTALS} />
				</Variant>
				<Variant
					label="5. Portals property"
					note="Lives in the detail sidebar. Says when a portal would still hide the item, and fixes it in one click."
				>
					<ControlPropertyPanel portals={PORTALS} />
				</Variant>
			</div>
		</div>
	)
}
